class FeatureShowcase {
  constructor(container) {
    this.container = container;
    this.featureButtons = container.querySelectorAll('.feature-showcase__feature-button');
    this.mediaElements = container.querySelectorAll('.feature-showcase__media');
    this.currentIndex = 0;
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.setInitialState();
  }

  bindEvents() {
    this.featureButtons.forEach((button, index) => {
      button.addEventListener('click', () => this.showFeature(index));
    });
    
    // Handle keyboard navigation
    this.container.addEventListener('keydown', (e) => this.handleKeyboard(e));
  }

  setInitialState() {
    // Ensure first feature is active
    this.updateActiveStates(0);
    
    // Reset all media elements and make first one active
    this.mediaElements.forEach((media, index) => {
      media.classList.remove('active', 'exiting');
      
      // Clean up any previous deferred media state
      this.cleanupDeferredMedia(media);
      
      if (index === 0) {
        media.classList.add('active');
        this.playMedia(media);
      } else {
        this.pauseMedia(media);
      }
    });
  }

  showFeature(index) {
    if (index === this.currentIndex) return;
    
    // Update button states
    this.updateActiveStates(index);
    
    // Simple media switch without transitions
    this.switchMedia(this.currentIndex, index);
    
    this.currentIndex = index;
  }

  updateActiveStates(activeIndex) {
    this.featureButtons.forEach((button, index) => {
      button.classList.toggle('active', index === activeIndex);
      button.setAttribute('aria-expanded', index === activeIndex ? 'true' : 'false');
    });
  }

  switchMedia(fromIndex, toIndex) {
    const currentMedia = this.mediaElements[fromIndex];
    const nextMedia = this.mediaElements[toIndex];
    
    // Immediately pause current media and remove active state
    if (currentMedia) {
      this.pauseMedia(currentMedia);
      currentMedia.classList.remove('active');
      this.cleanupDeferredMedia(currentMedia);
    }
    
    // Immediately activate next media
    if (nextMedia) {
      nextMedia.classList.add('active');
      this.playMedia(nextMedia);
    }
  }

  cleanupDeferredMedia(mediaElement) {
    const deferredMedia = mediaElement.querySelector('deferred-media');
    if (deferredMedia) {
      // Pause any video first
      const video = deferredMedia.querySelector('video');
      const iframe = deferredMedia.querySelector('iframe');
      
      if (video && !video.paused) {
        video.pause();
      }
      
      if (iframe) {
        this.pauseIframe(iframe);
      }
      
      // Remove loaded attribute to reset state
      deferredMedia.removeAttribute('loaded');
      
      // Show the poster again
      const poster = deferredMedia.querySelector('.deferred-media__poster');
      if (poster) {
        poster.style.display = '';
      }
      
      // Remove any dynamically loaded content (but keep template intact)
      const loadedContent = deferredMedia.querySelector('video:not(template video), iframe:not(template iframe)');
      if (loadedContent && loadedContent.parentNode === deferredMedia) {
        loadedContent.remove();
      }
    }
  }

  playMedia(mediaElement) {
    // Skip if no media element
    if (!mediaElement) return;
    
    const deferredMedia = mediaElement.querySelector('deferred-media');
    const placeholder = mediaElement.querySelector('.feature-showcase__placeholder');
    
    // If this is just a placeholder (no video set), do nothing
    if (placeholder && !deferredMedia) {
      return;
    }
    
    // Handle deferred media (videos)
    if (deferredMedia) {
      // Check if media is already loaded
      const loadedVideo = deferredMedia.querySelector('video');
      const loadedIframe = deferredMedia.querySelector('iframe');
      
      if (loadedVideo) {
        // Video is already loaded, just play it
        this.playVideoElement(loadedVideo);
      } else if (loadedIframe) {
        // Iframe is loaded, send play command
        this.playIframe(loadedIframe);
      } else if (!deferredMedia.getAttribute('loaded')) {
        // Need to load the media first
        this.loadDeferredMedia(deferredMedia);
      }
    } else {
      // Handle direct video elements (if any)
      const video = mediaElement.querySelector('video');
      if (video) {
        this.playVideoElement(video);
      }
    }
  }

  loadDeferredMedia(deferredMedia) {
    const poster = deferredMedia.querySelector('.deferred-media__poster');
    if (!poster) return;
    
    // Check if already loaded
    if (deferredMedia.getAttribute('loaded')) {
      const video = deferredMedia.querySelector('video');
      const iframe = deferredMedia.querySelector('iframe');
      
      if (video) {
        this.playVideoElement(video);
      } else if (iframe) {
        this.playIframe(iframe);
      }
      return;
    }
    
    // Trigger the deferred media loading by clicking the poster
    poster.click();
    
    // Wait for content to load and then play
    let attempts = 0;
    const maxAttempts = 30; // 3 seconds max wait time
    
    const checkForContent = () => {
      attempts++;
      
      const video = deferredMedia.querySelector('video');
      const iframe = deferredMedia.querySelector('iframe');
      
      if (video) {
        // Video loaded, play it
        setTimeout(() => this.playVideoElement(video), 100);
      } else if (iframe) {
        // Iframe loaded, send play command
        setTimeout(() => this.playIframe(iframe), 300);
      } else if (attempts < maxAttempts && !deferredMedia.getAttribute('loaded')) {
        // Still loading, check again
        setTimeout(checkForContent, 100);
      }
    };
    
    // Start checking after a short delay
    setTimeout(checkForContent, 100);
  }

  playVideoElement(video) {
    if (!video || typeof video.play !== 'function') return;
    
    try {
      video.currentTime = 0;
      video.muted = false; // Enable sound by default
      
      // Handle the play promise properly
      const playPromise = video.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn('Video autoplay with sound failed:', error);
          // If autoplay with sound fails, fallback to muted autoplay
          video.muted = true;
          video.play().catch(e => {
            console.warn('Video play failed completely:', e);
          });
        });
      }
    } catch (error) {
      console.warn('Error playing video:', error);
    }
  }

  playIframe(iframe) {
    if (!iframe || !iframe.src) return;
    
    try {
      if (iframe.src.includes('youtube')) {
        iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        iframe.contentWindow.postMessage('{"event":"command","func":"unMute","args":""}', '*');
      } else if (iframe.src.includes('vimeo')) {
        iframe.contentWindow.postMessage('{"method":"play"}', '*');
        iframe.contentWindow.postMessage('{"method":"setVolume","value":1}', '*');
      }
    } catch (e) {
      // Ignore cross-origin errors
      console.warn('Failed to control iframe:', e);
    }
  }

  pauseMedia(mediaElement) {
    // Skip if no media element
    if (!mediaElement) return;
    
    // Pause direct video elements
    const video = mediaElement.querySelector('video');
    if (video && !video.paused) {
      video.pause();
    }
    
    // Pause deferred media videos
    const deferredMedia = mediaElement.querySelector('deferred-media');
    if (deferredMedia) {
      const deferredVideo = deferredMedia.querySelector('video');
      if (deferredVideo && !deferredVideo.paused) {
        deferredVideo.pause();
      }
      
      // Also pause any iframes (YouTube/Vimeo)
      const iframe = deferredMedia.querySelector('iframe');
      if (iframe) {
        this.pauseIframe(iframe);
      }
    }
  }

  pauseIframe(iframe) {
    if (!iframe || !iframe.src) return;
    
    try {
      if (iframe.src.includes('youtube')) {
        iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } else if (iframe.src.includes('vimeo')) {
        iframe.contentWindow.postMessage('{"method":"pause"}', '*');
      }
    } catch (e) {
      // Ignore cross-origin errors
      console.warn('Failed to pause iframe:', e);
    }
  }

  handleKeyboard(event) {
    const { key } = event;
    
    switch (key) {
      case 'ArrowUp':
        event.preventDefault();
        this.showPreviousFeature();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.showNextFeature();
        break;
      case 'Home':
        event.preventDefault();
        this.showFeature(0);
        break;
      case 'End':
        event.preventDefault();
        this.showFeature(this.featureButtons.length - 1);
        break;
    }
  }

  showNextFeature() {
    const nextIndex = (this.currentIndex + 1) % this.featureButtons.length;
    this.showFeature(nextIndex);
  }

  showPreviousFeature() {
    const prevIndex = this.currentIndex === 0 
      ? this.featureButtons.length - 1 
      : this.currentIndex - 1;
    this.showFeature(prevIndex);
  }

  destroy() {
    // Remove event listeners
    this.featureButtons.forEach(button => {
      button.removeEventListener('click', this.showFeature);
    });
    
    this.container.removeEventListener('keydown', this.handleKeyboard);
  }
}

// Initialize all feature showcases on the page
document.addEventListener('DOMContentLoaded', () => {
  const showcases = document.querySelectorAll('.feature-showcase');
  
  showcases.forEach(showcase => {
    if (!showcase.featureShowcaseInstance) {
      showcase.featureShowcaseInstance = new FeatureShowcase(showcase);
    }
  });
});

// Handle Shopify theme editor events
if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const showcase = event.target.querySelector('.feature-showcase');
    if (showcase && !showcase.featureShowcaseInstance) {
      showcase.featureShowcaseInstance = new FeatureShowcase(showcase);
    }
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    const showcase = event.target.querySelector('.feature-showcase');
    if (showcase && showcase.featureShowcaseInstance) {
      showcase.featureShowcaseInstance.destroy();
      showcase.featureShowcaseInstance = null;
    }
  });
  
  document.addEventListener('shopify:block:select', (event) => {
    const showcase = event.target.closest('.feature-showcase');
    if (showcase && showcase.featureShowcaseInstance) {
      const feature = event.target.closest('.feature-showcase__feature');
      if (feature) {
        const blockIndex = Array.from(feature.parentNode.children).indexOf(feature);
        if (blockIndex >= 0) {
          showcase.featureShowcaseInstance.showFeature(blockIndex);
        }
      }
    }
  });
}
